import { BusinessHourModel } from '../../../models/OperationHoursModel';
import { Interval, isWithinInterval, parse, toDate, format } from 'date-fns';
import { FormDataModel } from '../../utils/FormUtils';
import React, { MouseEventHandler, ReactNode, useEffect, useRef } from 'react';
import { TextInput } from '@vp/swan';

export const formatStr = 'h:mm a';
export const parseStr = 'HH:mm';
const today = new Date();

export interface BusinessHourState {
  openingHour: Date;
  closingHour: Date;
}

export type FormHourModel = {
  data: FormDataModel<BusinessHourState>;
  errors: {
    invalidInterval: string;
    overlappingHours: string;
  };
};

export function makeTimePickerField(
  state: FormHourModel,
  key: keyof BusinessHourModel,
  updateHour: (value: Date, key: keyof BusinessHourModel) => void,
  timePickerClasses: string,
  hoursType: string
): ReactNode {
  const handleClick = (e: any) => {
    // Check for browser support before calling
    if (e.target && 'showPicker' in HTMLInputElement.prototype) {
      try {
        e.target.showPicker();
      } catch (error) {
        console.warn('Browser failed to open picker:', error);
      }
    }
  };

  return (
    <label aria-label={hoursType}>
      <TextInput
        onClick={handleClick}
        type="time"
        step="900"
        value={format(state.data[key] as Date, parseStr)}
        className={timePickerClasses}
        onChange={(event) => {
          const value = event.target.value;
          const timeValue = parse(value, parseStr, today);
          updateHour(timeValue, key);
        }}
      />
    </label>
  );
}

export function isOverlapping(
  operationHours: Array<BusinessHourModel>,
  hourIndex: number
): boolean {
  if (operationHours.length === 1) {
    return false;
  }
  const interval: BusinessHourModel = operationHours[hourIndex];

  // Skip validation if hours are not yet set
  if (!interval.openingHour || !interval.closingHour) {
    return false;
  }

  const openingHour = parse(interval.openingHour, formatStr, today);
  const closingHour = parse(interval.closingHour, formatStr, today);

  // Check if parsed dates are valid
  if (isNaN(openingHour.getTime()) || isNaN(closingHour.getTime())) {
    return false;
  }

  const overlappingHourIndex = operationHours
    .filter((val, index: number) => index !== hourIndex)
    .findIndex((value) => {
      // Skip if the other interval doesn't have both hours set
      if (!value.openingHour || !value.closingHour) {
        return false;
      }

      const otherStart = parse(value.openingHour, formatStr, today);
      const otherEnd = parse(value.closingHour, formatStr, today);

      // Skip if parsed dates are invalid
      if (isNaN(otherStart.getTime()) || isNaN(otherEnd.getTime())) {
        return false;
      }

      const otherInterval: Interval = {
        start: otherStart,
        end: otherEnd,
      };

      // Skip intervals where start is after end (invalid intervals)
      if (otherStart.getTime() > otherEnd.getTime()) {
        return false;
      }

      const openingWithin = isWithinInterval(openingHour, otherInterval);
      const closingWithin = isWithinInterval(closingHour, otherInterval);
      const otherCompletelyWithin =
        openingHour.getTime() <= otherStart.getTime() &&
        closingHour.getTime() >= otherEnd.getTime();

      // Check if the current interval's opening or closing hour falls within the other interval
      return openingWithin || closingWithin || otherCompletelyWithin;
    });

  return overlappingHourIndex !== -1;
}
